// Self-contained slot-limit + payment/upgrade flow for TeamV2.
// Faithfully ported from pages/Team/Team.js (V1) — slot check, paid-plan slot
// purchase modal, and free-plan upgrade modal. Payment LOGIC IS COPIED VERBATIM
// from V1 (verifyCouponCode / handleUpgradePlanSelection / handlePaymentSubmit
// + both modal JSX blocks) so behaviour is identical. The original Team.js is
// untouched. Exposes:
//   useSlotGate(api) -> { ensureSlot, modals }
// where ensureSlot() returns true if a member can be added now, or false after
// opening the appropriate modal (upgrade for free, slot-purchase for paid).
import React, { useState, useRef, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Modal, Card, InputNumber, Button, Badge, Divider, Statistic, Alert, Typography, Space } from 'antd';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, CreditCard, Crown, ShoppingCart, Sparkles, TrendingUp, UserPlus, Zap, X } from 'lucide-react';
import { useApi } from '../../../hooks/useApi';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { useCurrency } from '../../../hooks/useCurrency';
import { openRazorpayCheckout } from '../../../utils/razorpayCheckout';
import { BILLING_INTERVALS, getIntervalLabel, getIntervalMonths } from '../../../utils/pricing';

const { Title, Text, Paragraph } = Typography;

export function useSlotGate() {
  const api = useApi();
  const { user } = useAuth();
  const { mode } = useTheme();
  const isDark = mode === 'dark';

  const [organization, setOrganization] = useState(null);
  const [billingCurrency, setBillingCurrency] = useState(null);
  const { formatPrice, symbol, getPrice } = useCurrency({ billingCurrency });

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedUpgradePlan, setSelectedUpgradePlan] = useState(null);
  const [processingUpgradePlan, setProcessingUpgradePlan] = useState(null);
  const [upgradeMemberCount, setUpgradeMemberCount] = useState({});
  const [pendingUser, setPendingUser] = useState(null);
  const [additionalSlotsQuantity, setAdditionalSlotsQuantity] = useState(1);
  const [couponCode, setCouponCode] = useState('');
  const [actualCouponId, setActualCouponId] = useState(null);
  const [couponValid, setCouponValid] = useState(null);
  const [couponVerifying, setCouponVerifying] = useState(false);
  const [couponDiscount, setCouponDiscount] = useState(null);
  const [loading, setLoading] = useState(false);
  // Billing term for the upgrade flow. Longer terms are Razorpay-only — Stripe
  // has no 3/6-month prices — so the selector is hidden for Stripe orgs and the
  // value stays 'monthly', which is exactly the previous behaviour.
  const [selectedInterval, setSelectedInterval] = useState('monthly');
  const couponTimeoutRef = useRef(null);
  const isRazorpayOrg = organization?.paymentGateway === 'razorpay';
  const upgradeInterval = isRazorpayOrg ? selectedInterval : 'monthly';

  const fetchOrganization = useCallback(async () => {
    try {
      const response = await api.get('/api/v1/billing/profile');
      if (response.data.success) {
        setOrganization(response.data.data);
        const d = response.data.data;
        setBillingCurrency(d.orgPlanCurrency || (d.paymentGateway === 'razorpay' ? 'INR' : 'USD'));
      }
    } catch (error) {
      console.error('Error fetching organization:', error);
    }
  }, [api]);

  useEffect(() => { fetchOrganization(); }, [fetchOrganization]);

  // ── verifyCouponCode (verbatim from V1 Team.js) ──
  const verifyCouponCode = async (code) => {
    if (!code || code.trim() === '') {
      setActualCouponId(null);
      setCouponValid(null);
      setCouponDiscount(null);
      return;
    }

    setCouponVerifying(true);
    try {
      // Get current member count (total slots = admin + members)
      let totalSeats = undefined;
      if (organization) {
        // Use current slots (includes admin)
        totalSeats = organization.paidMemberSlots || organization.usedMemberSlots || undefined;
      }
      
      const isRazorpay = organization?.paymentGateway === 'razorpay';
      const verifyUrl = isRazorpay
        ? '/api/v1/public/razorpay-billing/verify-coupon'
        : '/api/v1/public/billing/verify-coupon';

      const response = await api.post(verifyUrl, {
        couponCode: code.trim(),
        memberCount: totalSeats,
        organizationId: organization?._id,
      });

      if (response.data.success && response.data.data.valid) {
        setCouponValid(true);
        setCouponDiscount(response.data.data.discount);
        setActualCouponId(response.data.data.couponCode);
        toast.success(`Coupon applied! ${response.data.data.discount}`);
      } else {
        setCouponValid(false);
        setCouponDiscount(null);
        setActualCouponId(null);
        toast.error(response.data.message || 'Invalid coupon code');
      }
    } catch (error) {
      setCouponValid(false);
      setCouponDiscount(null);
      setActualCouponId(null);
      toast.error(error.response?.data?.message || 'Failed to verify coupon');
    } finally {
      setCouponVerifying(false);
    }
  };

  // ── handleUpgradePlanSelection (verbatim from V1 Team.js) ──
  const handleUpgradePlanSelection = async (plan) => {
    setSelectedUpgradePlan(plan);
    setProcessingUpgradePlan(plan);
    
    try {
      if (!user?.id) {
        toast.error('User not found. Please try again.');
        setProcessingUpgradePlan(null);
        return;
      }

      // Calculate minimum member count (current used - 1 for admin)
      const currentSlots = organization?.usedMemberSlots || 5; // All current members including admin
      const minMemberCount = Math.max(1, currentSlots - 1); // Minimum = current used - 1 (admin excluded)
      
      // Get member count for this plan (default to minimum if not set)
      const memberCount = upgradeMemberCount[plan] || minMemberCount;
      
      // Calculate total seats and price
      const totalSeats = memberCount + 1; // +1 for admin
      const planAmount = getPrice(plan, upgradeInterval);
      const totalAmount = totalSeats * planAmount;

      console.log('🔄 Upgrade Checkout Calculation:', {
        currentPaidSlots: organization?.paidMemberSlots,
        currentUsedSlots: organization?.usedMemberSlots,
        currentSlots: currentSlots,
        breakdown: `${totalSeats} total slots = 1 admin + ${memberCount} members`,
        memberCount: memberCount,
        totalSeats: totalSeats,
        plan: plan,
        planAmount: planAmount,
        totalAmount: totalAmount,
        calculation: `${totalSeats} seats × ${symbol}${planAmount} = ${symbol}${totalAmount}/month`,
        note: 'Upgrading from free plan: User can add additional members'
      });

      toast.loading(`Creating checkout for ${totalSeats} seats (${memberCount} members + 1 admin)...`, { id: 'upgrade-loading' });

      // Try Razorpay for Indian users
      const isRazorpay = organization?.paymentGateway === 'razorpay';
      if (isRazorpay) {
        try {
          const rzpRes = await api.post('/api/v1/public/razorpay-billing/organization-subscription-checkout', {
            userId: user.id, plan, memberCount, interval: upgradeInterval,
            ...(actualCouponId && { couponCode: actualCouponId })
          });
          if (rzpRes.data?.success && (rzpRes.data?.data?.subscriptionId || rzpRes.data?.data?.orderId)) {
            toast.dismiss('upgrade-loading');
            const rzpData = rzpRes.data.data;
            openRazorpayCheckout({
              razorpayKeyId: rzpData.razorpayKeyId, amount: rzpData.amount,
              currency: rzpData.currency, orderId: rzpData.orderId,
              subscriptionId: rzpData.subscriptionId,
              customerName: rzpData.customerName, customerEmail: rzpData.customerEmail,
              notes: rzpData.notes, description: `TeamGrid ${plan} Upgrade`,
              onSuccess: async (pr) => {
                try {
                  await api.post('/api/v1/public/razorpay-billing/verify-payment', {
                    razorpay_order_id: pr.razorpay_order_id, razorpay_payment_id: pr.razorpay_payment_id,
                    razorpay_signature: pr.razorpay_signature, razorpay_subscription_id: pr.razorpay_subscription_id,
                    userId: user.id,
                    organizationId: rzpData.notes.organizationId, plan, memberCount,
                    interval: upgradeInterval,
                    totalSeats: rzpData.totalSeats, type: rzpData.billingType === 'one_time' ? 'one_time_payment' : 'organization_subscription',
                  });
                  localStorage.setItem('showSlotPurchaseAfterUpgrade', 'true');
                  toast.success('Upgrade successful!');
                  setTimeout(() => { window.location.href = '/dashboard?payment=success'; }, 100);
                } catch { toast.error('Payment verification failed'); setProcessingUpgradePlan(null); }
              },
              onFailure: () => { toast.error('Payment failed'); setProcessingUpgradePlan(null); },
              onDismiss: () => { setProcessingUpgradePlan(null); },
            });
            return;
          }
        } catch { /* fall through to Stripe */ }
      }

      // Stripe flow
      const response = await api.post('/api/v1/public/billing/organization-subscription-checkout', {
        userId: user.id,
        plan: plan,
        memberCount: memberCount,
        ...(actualCouponId && { couponCode: actualCouponId })
      });

      if (response.data?.success) {
        toast.dismiss('upgrade-loading');
        const url = response.data?.data?.url;
        if (url) {
          localStorage.setItem('showSlotPurchaseAfterUpgrade', 'true');
          window.location.href = url;
        } else {
          toast.error('Checkout URL not received');
          setProcessingUpgradePlan(null);
        }
      } else {
        toast.dismiss('upgrade-loading');
        toast.error(response.data?.message || 'Failed to start checkout');
        setProcessingUpgradePlan(null);
      }
    } catch (error) {
      console.error('Upgrade plan selection error:', error);
      toast.error(error.response?.data?.message || 'Failed to process upgrade');
      setProcessingUpgradePlan(null);
    }
  };

  // ── handlePaymentSubmit (verbatim from V1 Team.js) ──
  const handlePaymentSubmit = async () => {
    if (!pendingUser) return;

    try {
      setLoading(true);
      
      // Get organization's locked plan
      const orgPlan = organization?.orgPlanSelection || 'standard';
      
      const isRazorpay = organization?.paymentGateway === 'razorpay';

      if (pendingUser.needsAdditionalSlots) {
        if (additionalSlotsQuantity < 1 || additionalSlotsQuantity > 100) {
          toast.error('Please enter a valid quantity (1-100)');
          setLoading(false);
          return;
        }

        if (isRazorpay) {
          // Razorpay flow for purchasing additional slots
          try {
            const rzpRes = await api.post('/api/v1/razorpay-billing/purchase-additional-slots', {
              userId: pendingUser.id || user.id,
              additionalSlots: additionalSlotsQuantity,
              ...(actualCouponId && { couponCode: actualCouponId })
            });
            if (rzpRes.data?.success && rzpRes.data?.data?.orderId) {
              const rzpData = rzpRes.data.data;
              openRazorpayCheckout({
                razorpayKeyId: rzpData.razorpayKeyId, amount: rzpData.amount,
                currency: rzpData.currency, orderId: rzpData.orderId,
                customerName: user?.firstName + ' ' + user?.lastName,
                customerEmail: user?.email,
                notes: rzpData.notes, description: `TeamGrid Additional Slots`,
                onSuccess: async (pr) => {
                  try {
                    await api.post('/api/v1/razorpay-billing/verify-payment', {
                      razorpay_order_id: pr.razorpay_order_id, razorpay_payment_id: pr.razorpay_payment_id,
                      razorpay_signature: pr.razorpay_signature,
                      type: 'additional_slots', additionalSlots: additionalSlotsQuantity,
                    });
                    toast.success('Slots purchased!');
                    setTimeout(() => { window.location.reload(); }, 500);
                  } catch { toast.error('Payment verification failed'); setLoading(false); }
                },
                onFailure: () => { toast.error('Payment failed'); setLoading(false); },
                onDismiss: () => { setLoading(false); },
              });
              return;
            }
          } catch { /* fall through to Stripe */ }
        }

        const response = await api.post('/api/v1/billing/purchase-additional-slots', {
          userId: pendingUser.id || user.id,
          additionalSlots: additionalSlotsQuantity,
          ...(actualCouponId && { couponCode: actualCouponId })
        });
        
        if (response.data.success) {
          window.location.href = response.data.data.url;
        } else {
          toast.error('Failed to create checkout session');
          setLoading(false);
        }
      } else {
        // Legacy: Per-user subscription
        if (isRazorpay) {
          try {
            const rzpRes = await api.post('/api/v1/public/razorpay-billing/user-subscription-checkout', {
              userId: pendingUser.id, plan: orgPlan,
              ...(actualCouponId && { couponCode: actualCouponId })
            });
            if (rzpRes.data?.success && (rzpRes.data?.data?.subscriptionId || rzpRes.data?.data?.orderId)) {
              const rzpData = rzpRes.data.data;
              openRazorpayCheckout({
                razorpayKeyId: rzpData.razorpayKeyId,
                subscriptionId: rzpData.subscriptionId,
                orderId: rzpData.orderId,
                amount: rzpData.amount, currency: rzpData.currency,
                customerName: pendingUser.firstName + ' ' + pendingUser.lastName,
                customerEmail: pendingUser.email,
                notes: rzpData.notes, description: `TeamGrid ${orgPlan} Plan`,
                onSuccess: async (pr) => {
                  try {
                    await api.post('/api/v1/public/razorpay-billing/verify-payment', {
                      razorpay_order_id: pr.razorpay_order_id,
                      razorpay_subscription_id: pr.razorpay_subscription_id,
                      razorpay_payment_id: pr.razorpay_payment_id,
                      razorpay_signature: pr.razorpay_signature,
                      userId: pendingUser.id, plan: orgPlan, totalSeats: '1', type: rzpData.billingType === 'one_time' ? 'one_time_payment' : 'user_subscription',
                    });
                    toast.success('Payment successful!');
                    setTimeout(() => { window.location.reload(); }, 500);
                  } catch { toast.error('Payment verification failed'); setLoading(false); }
                },
                onFailure: () => { toast.error('Payment failed'); setLoading(false); },
                onDismiss: () => { setLoading(false); },
              });
              return;
            }
          } catch { /* fall through to Stripe */ }
        }

        const response = await api.post('/api/v1/billing/user-subscription-checkout', {
          userId: pendingUser.id, plan: orgPlan,
          ...(actualCouponId && { couponCode: actualCouponId })
        });
      
        if (response.data.success) {
          window.location.href = response.data.data.url;
        } else {
          toast.error('Failed to create checkout session');
          setLoading(false);
        }
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.message || 'Failed to process payment');
      setLoading(false);
    }
  };

  // ── Slot gate (ported from V1 openAddPanel pre-add check) ──
  // Returns true if a member can be added now; otherwise opens the right modal
  // (upgrade for free plans, slot-purchase for paid plans) and returns false.
  const ensureSlot = useCallback(async () => {
    await fetchOrganization();
    const updatedOrg = await api.get('/api/v1/billing/profile').then((res) => res.data.data).catch(() => organization);
    const isFreePlan = updatedOrg?.orgPlanSelection === 'free';
    const isPaidPlan = updatedOrg?.orgPlanSelection === 'standard' || updatedOrg?.orgPlanSelection === 'premium';

    if (updatedOrg?.paymentBypassEnabled) return true; // unlimited

    if ((isPaidPlan || isFreePlan) && (updatedOrg?.remainingSlots || 0) <= 0) {
      setOrganization(updatedOrg);
      if (isFreePlan) {
        toast.error('You have used all 5 free slots. Please upgrade your plan to add more members.');
        setShowUpgradeModal(true);
        return false;
      }
      toast.error('All slots are exhausted. Please purchase additional slots to add a team member.');
      setPendingUser({ id: null, name: 'New Team Member', email: '', needsAdditionalSlots: true });
      setAdditionalSlotsQuantity(1);
      setShowPaymentModal(true);
      return false;
    }
    return true;
  }, [api, organization, fetchOrganization]);

  /* ADDITIVE: ensureSlot() only opens a modal when slots have run out, because
     its job is to gate "add a member". A "Buy seats" button has to open the
     same flow on demand, whether or not seats are left — so this opens it
     directly. ensureSlot is untouched. */
  const openSlotPurchase = useCallback(async () => {
    await fetchOrganization();
    const org = await api.get('/api/v1/billing/profile').then((r) => r.data.data).catch(() => organization);
    if (org) setOrganization(org);
    if (org?.paymentBypassEnabled) {
      toast('Seats are unlimited on this organisation.');
      return;
    }
    if (org?.orgPlanSelection === 'free') {
      // Free plans have no slots to buy — the way up is a plan change.
      setShowUpgradeModal(true);
      return;
    }
    setPendingUser({ id: null, name: 'New Team Member', email: '', needsAdditionalSlots: true });
    setAdditionalSlotsQuantity(1);
    setShowPaymentModal(true);
  }, [api, organization, fetchOrganization]);

  // Modals element — render once in the page.
  const modals = (
    <>
      {/* Upgrade Plan Modal - For Free Plan Users */}
      <Modal
        open={showUpgradeModal}
        onCancel={() => {
          setShowUpgradeModal(false);
          setSelectedUpgradePlan(null);
          setUpgradeMemberCount({}); // Reset member count when modal closes
        }}
        footer={null}
        closable={true}
        width={900}
        className="upgrade-plan-modal"
        styles={{
          mask: { backgroundColor: isDark ? 'rgba(0, 0, 0, 0.75)' : 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(8px)' },
          content: { backgroundColor: 'transparent', boxShadow: 'none', padding: 0 }
        }}
      >
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3 }}
            className={`${
              isDark 
                ? 'bg-gradient-to-br from-zinc-900/95 via-zinc-800/95 to-zinc-900/95 border border-white/10' 
                : 'bg-white border border-gray-200 shadow-xl'
            } rounded-2xl p-8 backdrop-blur-xl`}
          >
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-center mb-8"
            >
              <div className="flex items-center justify-center mb-4">
                <Sparkles className="w-8 h-8 text-yellow-400 mr-2" />
                <Title level={2} className={`!mb-0 !text-3xl !font-bold ${isDark ? '!text-white' : '!text-gray-900'}`}>
                  Upgrade Your Plan
                </Title>
              </div>
              <Paragraph className={`!text-base !mb-0 ${isDark ? '!text-gray-400' : '!text-gray-600'}`}>
                You've used all 5 free slots. Upgrade to add more team members.
              </Paragraph>
            </motion.div>

            {/* Billing term selector — Razorpay only (Stripe has no 3/6-month prices) */}
            {isRazorpayOrg && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="flex justify-center mb-6"
              >
                <div
                  className={`inline-flex flex-wrap justify-center gap-1 p-1 rounded-xl border ${
                    isDark ? 'bg-white/5 border-white/10' : 'bg-gray-100 border-gray-200'
                  }`}
                >
                  {BILLING_INTERVALS.map((opt) => {
                    const active = selectedInterval === opt.key;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => setSelectedInterval(opt.key)}
                        disabled={processingUpgradePlan !== null}
                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                          active
                            ? 'bg-blue-600 text-white'
                            : isDark
                              ? 'text-gray-300 hover:bg-white/10'
                              : 'text-gray-700 hover:bg-white'
                        } ${processingUpgradePlan !== null ? 'opacity-60 cursor-not-allowed' : ''}`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* Plan Cards */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {/* Standard Plan */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                whileHover={{ scale: 1.02, y: -5 }}
              >
                <Card
                  className={`h-full border-2 transition-all cursor-pointer ${
                    selectedUpgradePlan === 'standard'
                      ? `border-blue-400 ${isDark ? 'bg-blue-400/10 shadow-xl shadow-blue-500/30' : 'bg-blue-50 shadow-xl shadow-blue-500/10'}`
                      : isDark 
                        ? 'border-white/20 bg-white/5 hover:border-white/40'
                        : 'border-gray-200 bg-white hover:border-gray-300 shadow-md'
                  }`}
                  style={{ borderRadius: '16px' }}
                  onClick={() => setSelectedUpgradePlan('standard')}
                >
                  <div className="text-center">
                    <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                      isDark ? 'bg-blue-500/20' : 'bg-blue-100'
                    }`}>
                      <Zap className={`w-8 h-8 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                    </div>
                    <Title level={4} className={`!mb-2 !text-xl ${isDark ? '!text-white' : '!text-gray-900'}`}>Standard</Title>
                    <div className="mb-6">
                      <span className={`text-4xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatPrice('standard')}</span>
                      <span className={`text-sm ml-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>/member/month</span>
                    </div>
                    <Space direction="vertical" size="middle" className="w-full mb-6 text-left">
                      <div className={`flex items-center ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        <Check className="w-4 h-4 text-blue-400 mr-2 flex-shrink-0" />
                        <span className="text-sm">All features</span>
                      </div>
                      <div className={`flex items-center ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        <Check className="w-4 h-4 text-blue-400 mr-2 flex-shrink-0" />
                        <span className="text-sm">Unlimited team members</span>
                      </div>
                      <div className={`flex items-center ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        <Check className="w-4 h-4 text-blue-400 mr-2 flex-shrink-0" />
                        <span className="text-sm">Advanced analytics</span>
                      </div>
                      <div className={`flex items-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        <X className={`w-4 h-4 mr-2 flex-shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                        <span className="text-sm">No Org Intelligence</span>
                      </div>
                    </Space>
                    
                    {/* Member Count Input Section */}
                    <div className={`mb-4 p-3 rounded-lg border ${
                      isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'
                    }`}>
                      <div className="mb-2">
                        <Text className={`text-xs block mb-1.5 font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          Number of Team Members
                        </Text>
                        {(() => {
                          // Calculate minimum member count (current used - 1 for admin)
                          const currentSlots = organization?.usedMemberSlots || 5;
                          const minMemberCount = Math.max(1, currentSlots - 1);
                          const memberCount = upgradeMemberCount['standard'] || minMemberCount;
                          const totalSeats = memberCount + 1; // +1 for admin
                          const termPrice = totalSeats * getPrice('standard', upgradeInterval);

                          return (
                            <>
                              <InputNumber
                                min={minMemberCount}
                                max={1000}
                                value={memberCount}
                                onChange={(value) => {
                                  setUpgradeMemberCount(prev => ({
                                    ...prev,
                                    'standard': value || minMemberCount
                                  }));
                                }}
                                controls={true}
                                className="w-full"
                                style={{ width: '100%' }}
                                size="small"
                              />
                              <Text className={`text-xs block mt-1.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                Currently using: {currentSlots} slots (1 admin + {currentSlots - 1} members)
                              </Text>
                              <Text className={`text-xs block mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                Total seats: {totalSeats} (1 admin + {memberCount} members)
                              </Text>
                              <Divider className="!my-2 !border-gray-300/50" />
                              <div className="text-center">
                                <Text className={`text-xs block ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                  {getIntervalLabel(upgradeInterval)} Total
                                </Text>
                                <Text className={`text-lg font-bold block ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                  {symbol}{termPrice.toFixed(2)}
                                  <span className="text-xs font-normal">
                                    {upgradeInterval === 'monthly'
                                      ? '/month'
                                      : ` every ${getIntervalMonths(upgradeInterval)} months`}
                                  </span>
                                </Text>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                    <Button
                      type={selectedUpgradePlan === 'standard' ? 'primary' : 'default'}
                      size="large"
                      block
                      onClick={(e) => {
                        e.stopPropagation();
                        // If plan is not selected, just select it (don't start checkout)
                        if (selectedUpgradePlan !== 'standard') {
                          setSelectedUpgradePlan('standard');
                        } else {
                          // Plan is already selected, proceed to checkout
                          handleUpgradePlanSelection('standard');
                        }
                      }}
                      disabled={processingUpgradePlan !== null}
                      loading={processingUpgradePlan === 'standard'}
                      className={selectedUpgradePlan === 'standard' ? 'bg-blue-600 hover:bg-blue-700' : (isDark ? 'bg-white/10 hover:bg-white/20 border-white/20' : 'bg-gray-100 hover:bg-gray-200 border-gray-300 text-gray-800')}
                      style={{ height: '48px', borderRadius: '8px' }}
                    >
                      {processingUpgradePlan === 'standard' ? 'Processing...' : selectedUpgradePlan === 'standard' ? 'Proceed to Payment' : 'Select Standard'}
                    </Button>
                  </div>
                </Card>
              </motion.div>

              {/* Premium Plan */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                whileHover={{ scale: 1.02, y: -5 }}
              >
                <Card
                  className={`h-full border-2 transition-all cursor-pointer ${
                    selectedUpgradePlan === 'premium'
                      ? `border-purple-400 ${isDark ? 'bg-purple-400/10 shadow-xl shadow-purple-500/30' : 'bg-purple-50 shadow-xl shadow-purple-500/10'}`
                      : isDark 
                        ? 'border-white/20 bg-white/5 hover:border-white/40'
                        : 'border-gray-200 bg-white hover:border-gray-300 shadow-md'
                  }`}
                  style={{ borderRadius: '16px' }}
                  onClick={() => setSelectedUpgradePlan('premium')}
                >
                  <div className="text-center">
                    <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                      isDark ? 'bg-purple-500/20' : 'bg-purple-100'
                    }`}>
                      <Crown className={`w-8 h-8 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                    </div>
                    <Title level={4} className={`!mb-2 !text-xl ${isDark ? '!text-white' : '!text-gray-900'}`}>Premium</Title>
                    <div className="mb-6">
                      <span className={`text-4xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatPrice('premium')}</span>
                      <span className={`text-sm ml-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>/member/month</span>
                    </div>
                    <Space direction="vertical" size="middle" className="w-full mb-6 text-left">
                      <div className={`flex items-center ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        <Check className="w-4 h-4 text-purple-400 mr-2 flex-shrink-0" />
                        <span className="text-sm">All features</span>
                      </div>
                      <div className={`flex items-center ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        <Check className="w-4 h-4 text-purple-400 mr-2 flex-shrink-0" />
                        <span className="text-sm">Org Intelligence</span>
                      </div>
                      <div className={`flex items-center ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        <Check className="w-4 h-4 text-purple-400 mr-2 flex-shrink-0" />
                        <span className="text-sm">Priority support</span>
                      </div>
                      <div className={`flex items-center ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        <Check className="w-4 h-4 text-purple-400 mr-2 flex-shrink-0" />
                        <span className="text-sm">Advanced AI</span>
                      </div>
                    </Space>
                    
                    {/* Member Count Input Section */}
                    <div className={`mb-4 p-3 rounded-lg border ${
                      isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'
                    }`}>
                      <div className="mb-2">
                        <Text className={`text-xs block mb-1.5 font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          Number of Team Members
                        </Text>
                        {(() => {
                          // Calculate minimum member count (current used - 1 for admin)
                          const currentSlots = organization?.usedMemberSlots || 5;
                          const minMemberCount = Math.max(1, currentSlots - 1);
                          const memberCount = upgradeMemberCount['premium'] || minMemberCount;
                          const totalSeats = memberCount + 1; // +1 for admin
                          const termPrice = totalSeats * getPrice('premium', upgradeInterval);

                          return (
                            <>
                              <InputNumber
                                min={minMemberCount}
                                max={1000}
                                value={memberCount}
                                onChange={(value) => {
                                  setUpgradeMemberCount(prev => ({
                                    ...prev,
                                    'premium': value || minMemberCount
                                  }));
                                }}
                                controls={true}
                                className="w-full"
                                style={{ width: '100%' }}
                                size="small"
                              />
                              <Text className={`text-xs block mt-1.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                Currently using: {currentSlots} slots (1 admin + {currentSlots - 1} members)
                              </Text>
                              <Text className={`text-xs block mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                Total seats: {totalSeats} (1 admin + {memberCount} members)
                              </Text>
                              <Divider className="!my-2 !border-gray-300/50" />
                              <div className="text-center">
                                <Text className={`text-xs block ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                  {getIntervalLabel(upgradeInterval)} Total
                                </Text>
                                <Text className={`text-lg font-bold block ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                  {symbol}{termPrice.toFixed(2)}
                                  <span className="text-xs font-normal">
                                    {upgradeInterval === 'monthly'
                                      ? '/month'
                                      : ` every ${getIntervalMonths(upgradeInterval)} months`}
                                  </span>
                                </Text>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                    <Button
                      type={selectedUpgradePlan === 'premium' ? 'primary' : 'default'}
                      size="large"
                      block
                      onClick={(e) => {
                        e.stopPropagation();
                        // If plan is not selected, just select it (don't start checkout)
                        if (selectedUpgradePlan !== 'premium') {
                          setSelectedUpgradePlan('premium');
                        } else {
                          // Plan is already selected, proceed to checkout
                          handleUpgradePlanSelection('premium');
                        }
                      }}
                      disabled={processingUpgradePlan !== null}
                      loading={processingUpgradePlan === 'premium'}
                      className={selectedUpgradePlan === 'premium' ? 'bg-purple-600 hover:bg-purple-700' : (isDark ? 'bg-white/10 hover:bg-white/20 border-white/20' : 'bg-gray-100 hover:bg-gray-200 border-gray-300 text-gray-800')}
                      style={{ height: '48px', borderRadius: '8px' }}
                    >
                      {processingUpgradePlan === 'premium' ? 'Processing...' : selectedUpgradePlan === 'premium' ? 'Proceed to Payment' : 'Select Premium'}
                    </Button>
                  </div>
                </Card>
              </motion.div>
            </div>

            {/* Coupon Code Section - Show when plan is selected */}
            {(selectedUpgradePlan === 'standard' || selectedUpgradePlan === 'premium') && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className={`mb-6 p-4 rounded-lg border ${
                  isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'
                }`}
              >
                <label className={`block text-sm font-medium mb-2 ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Have a coupon or promotion code? (Optional)
                </label>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => {
                        const value = e.target.value.toUpperCase();
                        setCouponCode(value);
                        
                        // Clear previous timeout
                        if (couponTimeoutRef.current) {
                          clearTimeout(couponTimeoutRef.current);
                        }
                        
                        // Verify after user stops typing (debounce)
                        if (value.trim() === '') {
                          setActualCouponId(null);
                          setCouponValid(null);
                          setCouponDiscount(null);
                        } else {
                          // Debounce verification - wait 800ms after user stops typing
                          couponTimeoutRef.current = setTimeout(() => {
                            verifyCouponCode(value);
                          }, 800);
                        }
                      }}
                      onBlur={() => {
                        if (couponCode.trim() !== '') {
                          verifyCouponCode(couponCode);
                        }
                      }}
                      placeholder="Enter coupon or promotion code"
                      className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 ${
                        couponValid === true
                          ? isDark 
                            ? 'bg-green-500/10 border-green-500/50 text-white focus:ring-green-500/50 focus:border-green-500/50' 
                            : 'bg-green-50 border-green-500 text-gray-900 focus:ring-green-500 focus:border-green-500'
                          : couponValid === false
                            ? isDark 
                              ? 'bg-red-500/10 border-red-500/50 text-white focus:ring-red-500/50 focus:border-red-500/50' 
                              : 'bg-red-50 border-red-500 text-gray-900 focus:ring-red-500 focus:border-red-500'
                          : isDark 
                            ? 'bg-white/5 border-white/20 text-white placeholder-gray-500 focus:ring-blue-500/50 focus:border-blue-500/50' 
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500'
                      }`}
                      style={{ height: '40px' }}
                      disabled={couponVerifying}
                    />
                    {couponVerifying && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                      </div>
                    )}
                    {couponValid === true && !couponVerifying && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <Check className="h-5 w-5 text-green-500" />
                      </div>
                    )}
                    {couponValid === false && !couponVerifying && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <X className="h-5 w-5 text-red-500" />
                      </div>
                    )}
                  </div>
                  {couponCode && (
                    <button
                      type="button"
                      onClick={() => {
                        setCouponCode('');
                        setActualCouponId(null);
                        setCouponValid(null);
                        setCouponDiscount(null);
                      }}
                      className={`px-3 py-2 rounded-lg border transition-colors ${
                        isDark 
                          ? 'bg-white/10 border-white/20 text-gray-300 hover:bg-white/20' 
                          : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Clear
                    </button>
                  )}
                </div>
                {couponValid === true && couponDiscount && (
                  <Text className={`text-sm mt-2 block font-medium ${
                    isDark ? 'text-green-400' : 'text-green-600'
                  }`}>
                    ✅ Valid! {couponDiscount} will be applied at checkout
                  </Text>
                )}
                {couponValid === false && (
                  <Text className={`text-sm mt-2 block ${
                    isDark ? 'text-red-400' : 'text-red-600'
                  }`}>
                    ❌ Invalid coupon code. Please check and try again.
                  </Text>
                )}
                {couponCode && couponValid === null && !couponVerifying && (
                  <Text className={`text-xs mt-2 block ${
                    isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    💰 Enter coupon code to verify
                  </Text>
                )}
              </motion.div>
            )}

            {/* Info */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-center"
            >
              <Text className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                💡 After upgrading, you can purchase additional slots to add more team members
              </Text>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </Modal>


      {/* Payment Modal - Professional UI with Ant Design & Framer Motion */}
      <Modal
        open={showPaymentModal && pendingUser}
        onCancel={() => setShowPaymentModal(false)}
        footer={null}
        closable={true}
        width={600}
        className="payment-modal"
        styles={{
          mask: { backgroundColor: isDark ? 'rgba(0, 0, 0, 0.75)' : 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(8px)' },
          content: { backgroundColor: 'transparent', boxShadow: 'none', padding: 0 }
        }}
      >
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3 }}
            className={`${
              isDark 
                ? 'bg-gradient-to-br from-zinc-900/95 via-zinc-800/95 to-zinc-900/95 border border-white/10' 
                : 'bg-white border border-gray-200 shadow-xl'
            } rounded-2xl p-5 backdrop-blur-xl`}
          >
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-center mb-4"
            >
              <div className="flex items-center justify-center mb-2">
                {pendingUser?.needsAdditionalSlots ? (
                  <ShoppingCart className="w-6 h-6 text-yellow-400 mr-2" />
                ) : (
                  <CreditCard className="w-6 h-6 text-blue-400 mr-2" />
                )}
                <Title level={3} className={`!mb-0 !text-xl !font-bold ${isDark ? '!text-white' : '!text-gray-900'}`}>
                  {pendingUser?.needsAdditionalSlots ? 'Purchase Additional Slots' : 'Activate User'}
                </Title>
            </div>
              <Paragraph className={`!text-sm !mb-0 ${isDark ? '!text-gray-400' : '!text-gray-600'}`}>
                {pendingUser?.needsAdditionalSlots 
                  ? 'You need to purchase additional slots to activate this team member'
                  : 'Payment required to activate this team member'}
              </Paragraph>
            </motion.div>

            {/* User Info Card - Compact */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className={`mb-3 ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`} style={{ borderRadius: '12px', padding: '12px' }}>
                <div className="flex items-center space-x-3 mb-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    isDark ? 'bg-blue-500/20' : 'bg-blue-100'
                  }`}>
                    <UserPlus className={`w-5 h-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                </div>
                  <div className="flex-1">
                    <Title level={5} className={`!mb-0 !text-sm !font-bold ${isDark ? '!text-white' : '!text-gray-900'}`}>{pendingUser?.name}</Title>
                    <Text className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{pendingUser?.email}</Text>
                </div>
              </div>

                <Divider className={`!my-2 ${isDark ? '!border-white/10' : '!border-gray-200'}`} />

                <div className="flex justify-between items-center text-xs">
                  <Text className={isDark ? 'text-gray-300' : 'text-gray-700'}>Plan:</Text>
                  <Badge 
                    color={organization?.orgPlanSelection === 'premium' ? 'purple' : organization?.orgPlanSelection === 'standard' ? 'blue' : 'default'}
                    text={
                      <Text className={`text-xs font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {organization?.orgPlanSelection === 'free' ? `Free (${formatPrice('free')})` : 
                     organization?.orgPlanSelection === 'standard' ? `Standard (${formatPrice('standard')}/month)` : 
                     organization?.orgPlanSelection === 'premium' ? `Premium (${formatPrice('premium')}/month)` : `Standard (${formatPrice('standard')}/month)`}
                      </Text>
                    }
                  />
                </div>
                <div className="flex justify-between items-center text-xs mt-2">
                  <Text className={isDark ? 'text-gray-300' : 'text-gray-700'}>Cost per slot:</Text>
                  <Text className={`font-semibold text-xs ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {organization?.orgPlanSelection === 'premium' ? `${formatPrice('premium')}/month` : `${formatPrice('standard')}/month`}
                  </Text>
                </div>
              </Card>
            </motion.div>

            {/* Additional Slots Section - Compact */}
            {pendingUser?.needsAdditionalSlots && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="space-y-2 mb-3"
              >
                {/* Current Slots Status - Compact */}
                <Card className={`${isDark ? 'border-blue-500/20 bg-blue-500/10' : 'border-blue-200 bg-blue-50'}`} style={{ borderRadius: '12px', padding: '12px' }}>
                  <div className="flex items-center mb-2">
                    <TrendingUp className={`w-4 h-4 mr-2 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                    <Title level={5} className={`!mb-0 !text-xs ${isDark ? '!text-blue-300' : '!text-blue-700'}`}>Current Slots Status</Title>
                </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Statistic
                      title={<Text className={`text-xs ${isDark ? 'text-blue-200' : 'text-blue-700'}`}>Paid</Text>}
                      value={organization?.paidMemberSlots || 0}
                      valueStyle={{ color: isDark ? '#ffffff' : '#1e40af', fontSize: '16px', fontWeight: 'bold' }}
                    />
                    <Statistic
                      title={<Text className={`text-xs ${isDark ? 'text-blue-200' : 'text-blue-700'}`}>Used</Text>}
                      value={organization?.usedMemberSlots || 0}
                      valueStyle={{ color: isDark ? '#ffffff' : '#1e40af', fontSize: '16px', fontWeight: 'bold' }}
                    />
                    <Statistic
                      title={<Text className={`text-xs ${isDark ? 'text-red-400' : 'text-red-600'}`}>Remaining</Text>}
                      value={organization?.remainingSlots || 0}
                      valueStyle={{ color: isDark ? '#f87171' : '#dc2626', fontSize: '16px', fontWeight: 'bold' }}
                    />
              </div>
                </Card>

                {/* Purchase Section - Compact */}
                <Card className={`${isDark ? 'border-yellow-500/20 bg-yellow-500/10' : 'border-blue-200 bg-blue-50'}`} style={{ borderRadius: '12px', padding: '12px' }}>
                  <div className="flex items-center mb-2">
                    <Sparkles className={`w-4 h-4 mr-2 ${isDark ? 'text-yellow-400' : 'text-blue-600'}`} />
                    <Title level={5} className={`!mb-0 !text-xs ${isDark ? '!text-yellow-300' : '!text-blue-700'}`}>Purchase Additional Slots</Title>
            </div>

                  <div className="space-y-2">
                    <div>
                      <Text className={`font-medium block mb-1 text-xs ${isDark ? 'text-yellow-300' : 'text-blue-700'}`}>Number of Slots:</Text>
                      <InputNumber
                        min={1}
                        max={100}
                        value={additionalSlotsQuantity}
                        onChange={(value) => setAdditionalSlotsQuantity(value || 1)}
                        controls={true}
                        size="middle"
                        className="w-full"
                        style={{ width: '100%' }}
                      />
                    </div>

                    <Divider className={`!my-2 ${isDark ? '!border-yellow-500/20' : '!border-blue-200'}`} />

                    <div className="space-y-1.5">
                      <div className="flex justify-between items-center text-xs">
                        <Text className={isDark ? 'text-yellow-200' : 'text-blue-700'}>Slots:</Text>
                        <Text className={`font-semibold text-xs ${isDark ? 'text-white' : 'text-gray-900'}`}>{additionalSlotsQuantity} {additionalSlotsQuantity === 1 ? 'slot' : 'slots'}</Text>
                      </div>
                      <div className="flex justify-between items-center text-xs">
                        <Text className={isDark ? 'text-yellow-200' : 'text-blue-700'}>Cost per Slot:</Text>
                        <Text className={`font-semibold text-xs ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {symbol}{getPrice(organization?.orgPlanSelection || 'standard')}/month
                        </Text>
                      </div>
                      <Divider className={`!my-1.5 ${isDark ? '!border-yellow-500/20' : '!border-blue-200'}`} />
                      <div className={`flex justify-between items-center p-2 rounded-lg border ${
                        isDark ? 'bg-yellow-500/10 border-yellow-500/20' : 'bg-blue-100 border-blue-200'
                      }`}>
                        <Text className={`font-bold text-xs ${isDark ? 'text-yellow-300' : 'text-blue-700'}`}>Total Cost:</Text>
                        <Text className={`font-bold text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {symbol}{getPrice(organization?.orgPlanSelection || 'standard') * additionalSlotsQuantity}/month
                        </Text>
                      </div>
                      <div className={`flex justify-between items-center p-2 rounded-lg border ${
                        isDark ? 'bg-green-500/10 border-green-500/20' : 'bg-green-100 border-green-200'
                      }`}>
                        <Text className={`font-bold text-xs ${isDark ? 'text-green-300' : 'text-green-700'}`}>Total After Purchase:</Text>
                        <Text className={`font-bold text-base ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                          {(organization?.paidMemberSlots || 0) + additionalSlotsQuantity} slots
                        </Text>
                      </div>
                    </div>
                    
                    {/* Coupon Code Section */}
                    <div className="mt-3">
                      <label className={`block text-xs font-medium mb-1.5 ${
                        isDark ? 'text-yellow-300' : 'text-blue-700'
                      }`}>
                        Have a coupon or promotion code? (Optional)
                      </label>
                      <div className="flex gap-2">
                        <div className="flex-1 relative">
                          <input
                            type="text"
                            value={couponCode}
                            onChange={(e) => {
                              const value = e.target.value.toUpperCase();
                              setCouponCode(value);
                              
                              // Clear previous timeout
                              if (couponTimeoutRef.current) {
                                clearTimeout(couponTimeoutRef.current);
                              }
                              
                              // Verify after user stops typing (debounce)
                              if (value.trim() === '') {
                                setActualCouponId(null);
                                setCouponValid(null);
                                setCouponDiscount(null);
                              } else {
                                // Debounce verification - wait 800ms after user stops typing
                                couponTimeoutRef.current = setTimeout(() => {
                                  verifyCouponCode(value);
                                }, 800);
                              }
                            }}
                            onBlur={() => {
                              if (couponCode.trim() !== '') {
                                verifyCouponCode(couponCode);
                              }
                            }}
                            placeholder="Enter coupon code"
                            className={`w-full px-3 py-1.5 text-xs rounded-lg border focus:outline-none focus:ring-2 ${
                              couponValid === true
                                ? isDark 
                                  ? 'bg-green-500/10 border-green-500/50 text-white focus:ring-green-500/50 focus:border-green-500/50' 
                                  : 'bg-green-50 border-green-500 text-gray-900 focus:ring-green-500 focus:border-green-500'
                                : couponValid === false
                                  ? isDark 
                                    ? 'bg-red-500/10 border-red-500/50 text-white focus:ring-red-500/50 focus:border-red-500/50' 
                                    : 'bg-red-50 border-red-500 text-gray-900 focus:ring-red-500 focus:border-red-500'
                                  : isDark 
                                    ? 'bg-white/5 border-white/20 text-white placeholder-gray-500 focus:ring-blue-500/50 focus:border-blue-500/50' 
                                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500'
                            }`}
                            style={{ height: '32px', fontSize: '12px' }}
                            disabled={couponVerifying}
                          />
                          {couponVerifying && (
                            <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500"></div>
                            </div>
                          )}
                          {couponValid === true && !couponVerifying && (
                            <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                              <Check className="h-4 w-4 text-green-500" />
                            </div>
                          )}
                          {couponValid === false && !couponVerifying && (
                            <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                              <X className="h-4 w-4 text-red-500" />
                            </div>
                          )}
                        </div>
                        {couponCode && (
                          <button
                            type="button"
                            onClick={() => {
                              setCouponCode('');
                              setActualCouponId(null);
                              setCouponValid(null);
                              setCouponDiscount(null);
                            }}
                            className={`px-2 py-1.5 text-xs rounded-lg border transition-colors ${
                              isDark 
                                ? 'bg-white/10 border-white/20 text-gray-300 hover:bg-white/20' 
                                : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      {couponValid === true && couponDiscount && (
                        <Text className={`text-xs mt-1 block font-medium ${
                          isDark ? 'text-green-400' : 'text-green-600'
                        }`}>
                          ✅ Valid! {couponDiscount} will be applied
                        </Text>
                      )}
                      {couponValid === false && (
                        <Text className={`text-xs mt-1 block ${
                          isDark ? 'text-red-400' : 'text-red-600'
                        }`}>
                          ❌ Invalid coupon code
                        </Text>
                      )}
                    </div>
                  </div>
                </Card>

                {/* Warning Alert - Compact */}
                <Alert
                  message={<span className={`text-xs font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Slots Exhausted</span>}
                  description={<span className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>You have used all available slots. This purchase will add {additionalSlotsQuantity} {additionalSlotsQuantity === 1 ? 'additional slot' : 'additional slots'}.</span>}
                  type="warning"
                  showIcon
                  className={isDark ? 'border-yellow-500/20 bg-yellow-500/10' : 'border-orange-300 bg-orange-50'}
                  style={{ 
                    borderRadius: '8px', 
                    padding: '8px',
                    backgroundColor: isDark ? 'rgba(251, 191, 36, 0.1)' : 'rgba(255, 247, 237, 1)',
                    borderColor: isDark ? 'rgba(251, 191, 36, 0.2)' : 'rgba(251, 146, 60, 0.3)'
                  }}
                />
              </motion.div>
            )}

            {/* Info Card - Compact */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="mb-3"
            >
              <Alert
                message={<span className={`text-xs ${isDark ? 'text-white' : 'text-gray-900'}`}>Plan Features</span>}
                description={<span className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{organization?.orgPlanSelection === 'free' ? 'All users in your org must be on the Free plan.' : organization?.orgPlanSelection === 'standard' ? 'Includes all features except Organizational Intelligence.' : 'Includes all features including Organizational Intelligence.'}</span>}
                type="info"
                showIcon
                className={isDark ? 'border-blue-500/20 bg-blue-500/10' : 'border-blue-200 bg-blue-50'}
                style={{ borderRadius: '8px', padding: '8px' }}
              />
            </motion.div>

            {/* Action Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex space-x-2"
            >
              <Button
                size="middle"
                onClick={() => setShowPaymentModal(false)}
                disabled={loading}
                className={`flex-1 ${
                  isDark 
                    ? 'bg-white/10 hover:bg-white/20 border-white/20 text-white' 
                    : 'bg-gray-100 hover:bg-gray-200 border-gray-300 text-gray-800'
                }`}
                style={{ height: '40px', borderRadius: '8px' }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                size="middle"
                onClick={handlePaymentSubmit}
                disabled={loading}
                loading={loading}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                style={{ height: '40px', borderRadius: '8px' }}
                icon={<CreditCard className="w-3 h-3" />}
              >
                {loading ? 'Processing...' : 'Proceed to Payment'}
              </Button>
            </motion.div>

            <Text className={`text-xs block text-center mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              🔒 You'll be redirected to secure {organization?.paymentGateway === 'razorpay' ? 'Razorpay' : 'Stripe'} checkout
            </Text>
          </motion.div>
        </AnimatePresence>
      </Modal>
    </>
  );

  return { ensureSlot, openSlotPurchase, modals, organization, refreshOrg: fetchOrganization };
}
